import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { addToCart } from '../../services/api';
import { useCart } from '../../context/CartContext';
import PhotoCarousel from '../common/PhotoCarousel';
import { buildAssetUrl, getVariationTileImage } from '../../utils/imageResolver';
import { weightLine } from '../../utils/weight';
import { listingSexLabel } from '../../constants/enums';

/**
 * AnimalListingCard — I-02b Individual-Animal Selling.
 *
 * One purchasable animal: its own photos (gallery → variation/species
 * fallback), identity (name/sex/hatch), price (override or variation,
 * "Inquire" when neither), and Add to Cart pinned to THIS animal.
 */
const GENDER_SYMBOL = { 0: '♂', 1: '♀', 2: '?' };

function AnimalListingCard({ animal, variation, variationLabel, product, onAdded }) {
  const [busy, setBusy] = useState(false);
  const [added, setAdded] = useState(false);
  const [error, setError] = useState(null);
  const { cartItems, refreshCart } = useCart();

  // Cowork obs (full-flow sweep): "In Cart ✓" must survive reload —
  // derive from the cart itself, not just this-session click state.
  const inCart = added || (Array.isArray(cartItems) && cartItems.some(
    (i) => (i?.animal_id ?? i?.animal?.id) === animal.id
  ));

  // Card-size images prefer the 400px thumb rendition (thumb_url falls
  // back to the original server-side, so `|| g.url` is belt-and-braces).
  const gallery = (animal.gallery || []).map((g) => ({
    url: buildAssetUrl(g?.thumb_url || g?.url),
    thumbUrl: buildAssetUrl(g?.thumb_url || g?.url),
    credit: (g?.credit || '').trim() || null,
    creditInfo: g?.credit_info || null,  // #110e — license pill fallback
  })).filter((g) => g.url);
  // Cascade fallback: no animal photos yet → the variation/morph/species tile
  const fallback = getVariationTileImage(variation, product);
  const images = gallery.length > 0
    ? gallery
    : (fallback ? [{ url: fallback, thumbUrl: fallback, credit: null }] : []);

  // Q-sex-certainty (operator ruling 2026-10-14): the listing says HOW
  // the sex is known — "Temp-sexed female", "Probable male" — and plain
  // "Female" / "Male" when confirmed. ONE label for the card's two places.
  const sexLabel = listingSexLabel(animal.gender, animal.sex_basis);
  const displayName = animal.name
    || `${sexLabel} ${String(animal.id).slice(0, 6).toUpperCase()}`.trim();
  const price = animal.price != null ? Number(animal.price) : null;

  // Approximate age in months from out_of_water_date (30-day months).
  // Omitted entirely when the animal has no OOW date (e.g. geckos), or
  // when less than one full month has elapsed (incl. future-dated OOW) —
  // never fabricate "~1 month" for a freshly-listed animal.
  let ageMonths = null;
  if (animal.out_of_water_date) {
    const oow = new Date(animal.out_of_water_date);
    if (!Number.isNaN(oow.getTime())) {
      const months = Math.floor(
        (Date.now() - oow.getTime()) / (30 * 24 * 60 * 60 * 1000)
      );
      if (months >= 1) ageMonths = months;
    }
  }

  // I-02b.2 listing bar: the API only lists animals with a resolvable
  // price + own photos. A card without either is stale data — render
  // nothing rather than an un-buyable "Inquire" state (operator killed
  // that fallback 2026-07-31).
  if (price == null || images.length === 0) return null;

  // The animal's weight (operator request 2026-10-07): "42 g · weighed
  // Oct 7, 2026" when it has one, and NOTHING — no label, no "—" — when it
  // does not. `weightLine` answers null for a missing value.
  const weight = weightLine(animal.current_weight_g, animal.weighed_at);

  const handleAdd = async () => {
    setBusy(true);
    setError(null);
    try {
      await addToCart(variation.id, 1, animal.id);
      setAdded(true);
      // Sync CartContext so the navbar badge / other cards update
      // without a page navigation.
      if (refreshCart) refreshCart();
      if (onAdded) onAdded(animal);
    } catch (err) {
      // ADV-REVIEW #14: message can be a serializer-errors dict — never
      // render an object into React.
      const raw = err?.response?.data?.message;
      setError(typeof raw === 'string' ? raw : 'Could not add to cart');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="animal-listing-card variation-card card">
      {/* Audit item 10c (2026-08-14): pills pin to the image box
          (.listing-media-box is position:relative), not the card, so
          carousel reflow can never collide or displace them. */}
      <div className="listing-media-box">
        {/* No "This exact animal" overlay (operator, prod 2026-10-05:
            "every time we try to get clever there it just ends up looking
            dumb"). The card already says "Animal #… pictured". */}
        {/* Wrong-species guard (operator-found): an empty animal gallery
            means the image below is the variation/morph/species CASCADE
            fallback — stock imagery, not this animal. Say so, so a
            representative photo can never read as the animal being sold. */}
        {gallery.length === 0 && (
          <span className="listing-rep-photo-pill">Representative photo</span>
        )}
        <PhotoCarousel
          images={images}
          alt={displayName}
          hoverAdvance
          imgClassName="variation-image card-img-top"
        />
      </div>
      <div className="card-body variation-card-body">
        <h5 className="card-title mb-1"><strong>{displayName}</strong></h5>
        {/* I-02b.2 flat grid: every card names its variation so animals
            from different variations are distinguishable side by side */}
        {variationLabel && (
          <p className="card-text mb-1 animal-listing-variation">
            {variationLabel}
          </p>
        )}
        <p className="card-text mb-1 animal-listing-meta">
          <span className="animal-listing-sex">
            <span className="animal-listing-sex-icon" aria-hidden="true">
              {GENDER_SYMBOL[animal.gender] ?? '?'}
            </span>
            {sexLabel}
          </span>
          {/* operator 2026-08-01/02: frogs show out-of-water DATE (their
              meaningful age marker); geckos have no OOW → hatch date */}
          {animal.out_of_water_date
            ? ` · Out of water date ${animal.out_of_water_date}`
            : animal.hatch_date ? ` · Hatch date ${animal.hatch_date}` : ''}
          {ageMonths != null && ` · ~${ageMonths} month${ageMonths === 1 ? '' : 's'}`}
        </p>
        {weight && (
          <p className="card-text mb-1 animal-listing-weight" data-listing-weight>
            {weight}
          </p>
        )}
        {/* R-SHIP-AFTER (2026-10-28): "Ships from 4/12/2027" — the
            server's sentence, sent only while that day is in the future. */}
        {animal.ships_from_note && (
          <p className="card-text mb-1 animal-listing-ships-from" data-listing-ships-from>
            {animal.ships_from_note}
          </p>
        )}
        {/* Photo-is-the-animal marker (competitive benchmark 2026-08):
            when the gallery is this animal's OWN photos, say so with the
            same short id used across buyer-facing rosters (#FIRST6 of
            id, uppercase). The cascade-fallback case gets the
            "Representative photo" pill above instead — never both. */}
        {gallery.length > 0 && animal.id && (
          <p className="card-text mb-1 listing-pictured-note">
            Animal #{String(animal.id).slice(0, 6).toUpperCase()} pictured.
          </p>
        )}
        <p className="card-text mb-2 animal-listing-price">
          ${price.toFixed(2)}
        </p>
        {/* Audit item 10a (2026-08-14): shared CTA band across all
            listing cards — see .listing-cta-slot in products.css. */}
        <div className="listing-cta-slot">
          {error && <div className="text-danger mb-2" style={{ fontSize: '0.85rem' }}>{error}</div>}
          <button
            type="button"
            className="ff-btn ff-btn--primary ff-btn--block btn-add-to-cart"
            disabled={busy || inCart}
            onClick={handleAdd}
          >
            {inCart ? 'In Cart ✓' : busy ? 'Adding…' : 'Add to Cart'}
          </button>
        </div>
      </div>
    </div>
  );
}

AnimalListingCard.propTypes = {
  animal: PropTypes.object.isRequired,
  variation: PropTypes.object.isRequired,
  variationLabel: PropTypes.string,
  product: PropTypes.object,
  onAdded: PropTypes.func,
};

export default AnimalListingCard;
